import {
  Box, Checkbox, Divider, ListItemText, Menu, MenuItem,
} from '@mui/material';
import { FC } from 'react';
import {
  ALL_ROTATION_FILTERS, hasGroupNarrowing, hasRotationNarrowing, ROTATION_FILTER_LABELS,
  RotationFilterValue, VehicleFilterMode,
} from 'src/components/dispatch/vehicleFilter';
import { PlanningsGroupEntity } from 'src/entities/PlanningsGroup.entity';

/**
 * 車両リストの絞り込みメニュー（サンプル route-planner の MapPage 準拠）。
 * trigger Button は呼び出し側が持つ。
 *
 * モードは 1 つだけ選べる。選択中のものをもう一度押すと「すべて表示」へ戻す
 * （戻す動線がメニュー内に無いと、絞り込んだまま抜けられなくなる）。
 *
 * グループ多選択は groupEntities の中に id=0 名前="すべて表示する" の合成エントリを
 * 親側で先頭に prepend してもらう前提 (V3Plans Component.tsx と同じ方式)。
 */
type Props = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  mode: VehicleFilterMode;
  onModeChange: (mode: VehicleFilterMode) => void;
  /** 「選択中のみ表示」の活性条件 (= 行が 1 つ以上選択されている) */
  hasSelection: boolean;
  /** 先頭に id=0 「すべて表示する」を含む全グループ。groups が無いなら空配列 */
  groupEntities: PlanningsGroupEntity[];
  /** 現在チェック済みのグループ index 群 (0 = 「すべて表示する」を含む) */
  selectedGroupIndices: number[];
  onSelectedGroupIndicesChange: (next: number[]) => void;
  /**
   * チェック済みの回転。既定は全部（回転で絞っていない状態）。
   *
   * `onRotationsChange` を渡さない画面ではセクションごと出さない。回転は「1 日の中の
   * 何本目か」なので、レーンが期間全体にまたがる長距離配車では意味をなさない。
   */
  rotations?: readonly RotationFilterValue[];
  onRotationsChange?: (next: RotationFilterValue[]) => void;
  /**
   * 制約違反のある車両だけに絞るか。既定はチェックなし。
   *
   * `onConstraintViolationOnlyChange` を渡さない画面ではセクションごと出さない。判定は
   * 行ごとの積載量を要るので、それを持たない画面で出すと 1 台も残らない絞り込みになる。
   */
  constraintViolationOnly?: boolean;
  onConstraintViolationOnlyChange?: (next: boolean) => void;
};

const sectionLabelSx = {
  px: 2, py: 0.25, m: 0, fontSize: 10, fontWeight: 700,
  color: 'hsl(var(--muted-foreground))', letterSpacing: '0.05em',
} as const;

const itemSx = { fontSize: 12 } as const;

const STATUS_ITEMS: { mode: VehicleFilterMode; label: string }[] = [
  { mode: '配車済み車両のみ表示', label: '配車済の車両のみ' },
  { mode: '未配車の車両のみ表示', label: '未配車の車両のみ（計画無）' },
];

const TruckDisplayStatusMenu: FC<Props> = ({
  anchorEl, open, onClose, mode, onModeChange, hasSelection,
  groupEntities, selectedGroupIndices, onSelectedGroupIndicesChange,
  rotations, onRotationsChange, constraintViolationOnly, onConstraintViolationOnlyChange,
}) => {
  // 既定は「すべて表示する」＋全グループが選ばれた状態なので、選択の有無ではなく
  // index 0 の有無で判定する（呼び出し側のボタンラベルと同じ hasGroupNarrowing）
  const isFiltered = mode !== 'すべて表示'
    || hasGroupNarrowing(selectedGroupIndices)
    || hasRotationNarrowing(rotations)
    || !!constraintViolationOnly;
  // 出すかどうかは値ではなくハンドラの有無で決める（車両追加・確定ボタンと同じ分離）
  const showRotationFilter = !!onRotationsChange;
  const showConstraintFilter = !!onConstraintViolationOnlyChange;
  /** クリアは既定へ戻す操作。空にすると「全部のチェックを外した」意味になり 1 台も出なくなる */
  const allGroupIndices = groupEntities.map((_g, i) => i);

  // 同じモードをもう一度押したら解除
  const toggleMode = (next: VehicleFilterMode) => {
    onModeChange(mode === next ? 'すべて表示' : next);
    onClose();
  };

  /**
   * 回転はグループと同じ多選択。通知は必ず回転の順に並べる
   * （押した順のまま返すと、ボタンのラベルや保存値の並びが操作履歴で変わる）。
   */
  const toggleRotation = (value: RotationFilterValue) => {
    const current = rotations ?? ALL_ROTATION_FILTERS;
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onRotationsChange?.(ALL_ROTATION_FILTERS.filter((v) => next.includes(v)));
  };

  // V3Plans の TruckSelectMenuPresenter#handleCheckboxChange と同じロジック:
  //   - index=0 (すべて表示する) は ON/OFF で全選択 / 全解除のトグル
  //   - 個別グループの ON は追加、OFF は本人と「すべて表示する」両方を外す
  const handleCheckboxChange = (value: number) => {
    if (value === 0) {
      onSelectedGroupIndicesChange(
        selectedGroupIndices.includes(0) ? [] : groupEntities.map((_g, i) => i),
      );
      return;
    }
    if (selectedGroupIndices.includes(value)) {
      onSelectedGroupIndicesChange(selectedGroupIndices.filter((it) => ![0, value].includes(it)));
    } else {
      onSelectedGroupIndicesChange([...selectedGroupIndices, value]);
    }
  };

  const renderModeItem = ({ mode: itemMode, label }: { mode: VehicleFilterMode; label: string }) => (
    <MenuItem
      key={itemMode}
      selected={mode === itemMode}
      onClick={() => toggleMode(itemMode)}
      sx={itemSx}
    >
      {label}
    </MenuItem>
  );

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{ paper: { sx: { minWidth: 220 } } }}
    >
      {isFiltered && (
        <MenuItem
          onClick={() => {
            onModeChange('すべて表示');
            onSelectedGroupIndicesChange(allGroupIndices);
            onRotationsChange?.([...ALL_ROTATION_FILTERS]);
            onConstraintViolationOnlyChange?.(false);
            onClose();
          }}
          sx={{ ...itemSx, color: 'primary.main', fontWeight: 600 }}
        >
          フィルターをクリア
        </MenuItem>
      )}
      {isFiltered && <Divider sx={{ my: 0.5 }} />}

      <MenuItem
        selected={mode === '選択中のみ表示する'}
        disabled={!hasSelection}
        onClick={() => toggleMode('選択中のみ表示する')}
        sx={itemSx}
      >
        選択中のみ表示
      </MenuItem>

      <Divider sx={{ my: 0.5 }} />
      <Box component="p" sx={sectionLabelSx}>配車状況</Box>
      {STATUS_ITEMS.map(renderModeItem)}
      {/* 配車済み・未配車と違い、これは重ねられる条件なのでチェックボックスにする */}
      {showConstraintFilter && (
        <MenuItem
          selected={constraintViolationOnly}
          sx={{ ...itemSx, pl: 1 }}
          onClick={() => onConstraintViolationOnlyChange(!constraintViolationOnly)}
        >
          <Checkbox
            checked={!!constraintViolationOnly}
            onChange={(event) => event.stopPropagation()}
            size="small"
            sx={{ p: 0.5, mr: 0.5 }}
          />
          <ListItemText primary="制約違反のある車両のみ" primaryTypographyProps={{ fontSize: 12 }} />
        </MenuItem>
      )}

      {showRotationFilter && <Divider sx={{ my: 0.5 }} />}
      {showRotationFilter && <Box component="p" sx={sectionLabelSx}>回転</Box>}
      {showRotationFilter && ALL_ROTATION_FILTERS.map((value) => (
        <MenuItem
          key={`rotation-${value}`}
          selected={rotations?.includes(value)}
          sx={{ ...itemSx, pl: 1 }}
          onClick={() => toggleRotation(value)}
        >
          <Checkbox
            checked={!!rotations?.includes(value)}
            onChange={(event) => event.stopPropagation()}
            size="small"
            sx={{ p: 0.5, mr: 0.5 }}
            value={value}
          />
          <ListItemText primary={ROTATION_FILTER_LABELS[value]} primaryTypographyProps={{ fontSize: 12 }} />
        </MenuItem>
      ))}

      {groupEntities.length > 0 && <Divider sx={{ my: 0.5 }} />}
      {groupEntities.length > 0 && <Box component="p" sx={sectionLabelSx}>グループ</Box>}
      {groupEntities.map((g, index) => (
        <MenuItem
          key={['truckSelectMenu', g.id, g.name].join('-')}
          selected={selectedGroupIndices.includes(index)}
          sx={{ ...itemSx, pl: 1 }}
          onClick={() => handleCheckboxChange(index)}
        >
          <Checkbox
            checked={selectedGroupIndices.includes(index)}
            onChange={(event) => event.stopPropagation()}
            size="small"
            sx={{ p: 0.5, mr: 0.5 }}
            value={index}
          />
          <ListItemText primary={g.name} primaryTypographyProps={{ fontSize: 12 }} />
          <Box component="span" sx={{ ml: 'auto', pl: 1, fontSize: 10, color: 'hsl(var(--muted-foreground))' }}>
            {`${g.truckIds.length}台`}
          </Box>
        </MenuItem>
      ))}
    </Menu>
  );
};

export default TruckDisplayStatusMenu;
